import { Link } from 'react-router-dom';
import { Lock, Sparkles, Disc3 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import useSubscription from '@/hooks/useSubscription';

/*
 * LockedOverlay
 *
 * Shown on the Upload page when the user's trial has ended and they
 * haven't upgraded. Crates are still readable elsewhere — only the
 * scan action is gated. Copy emphasizes that their work isn't gone yet
 * (30-day grace period) so users don't panic.
 */
export default function LockedOverlay() {
  const { isLocked, graceDaysLeft } = useSubscription();
  if (!isLocked) return null;

  return (
    <div
      className="max-w-xl mx-auto rounded-2xl border border-border bg-card p-6 sm:p-8 text-center space-y-4"
      data-testid="upload-locked-overlay"
    >
      <div className="mx-auto w-14 h-14 rounded-2xl bg-[hsl(var(--primary))] grid place-items-center text-[hsl(var(--primary-foreground))]">
        <Lock className="w-6 h-6" />
      </div>
      <h2 className="font-display text-2xl sm:text-3xl font-semibold">
        Your trial has ended
      </h2>
      <p className="text-sm text-muted-foreground max-w-md mx-auto">
        Your scanned crates are safe and still readable
        {graceDaysLeft != null ? (
          <> for <strong className="text-foreground">{graceDaysLeft} more {graceDaysLeft === 1 ? 'day' : 'days'}</strong></>
        ) : (
          <> for the next 30 days</>
        )}
        . Subscribe to GroovLens Pro to start scanning again and keep your
        collection intact.
      </p>
      <div className="flex flex-wrap items-center justify-center gap-3 pt-2">
        <Link to="/pricing">
          <Button size="lg" className="rounded-xl" data-testid="locked-upgrade-button">
            <Sparkles className="w-4 h-4 mr-1.5" /> See plans
          </Button>
        </Link>
        <Link to="/crate">
          <Button variant="secondary" size="lg" className="rounded-xl" data-testid="locked-view-crates-button">
            <Disc3 className="w-4 h-4 mr-1.5" /> View my crates
          </Button>
        </Link>
      </div>
      <p className="text-[11px] text-muted-foreground/80 pt-2">
        Cancel any time. Your account stays open even if your subscription lapses.
      </p>
    </div>
  );
}
